import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Divider
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';
import { ar } from 'date-fns/locale';

interface DocumentAlert {
  _id: string;
  documentName: string;
  documentNumber: string;
  expiryDate: string;
  notes?: string;
}

const DocumentAlertsWidget: React.FC = () => {
  const [data, setData] = useState<DocumentAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // جلب المستندات التي تنتهي خلال 14 يوم
      const response = await axios.get('/api/settings/document-alerts/expiring', {
        params: { days: 14 }
      });
      setData(response.data.documents);
      setError(null);
    } catch (err) {
      setError('فشل في تحميل التنبيهات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = (expiryDate: string): number => {
    return differenceInDays(new Date(expiryDate), new Date());
  };

  const getStatusColor = (days: number) => {
    if (days < 0) return { bg: '#fee', color: '#e74c3c', label: 'منتهي' };
    if (days === 0) return { bg: '#fee', color: '#e74c3c', label: 'ينتهي اليوم' };
    if (days <= 7) return { bg: '#fff3cd', color: '#f39c12', label: `${days} أيام` };
    return { bg: '#fff3cd', color: '#f39c12', label: `${days} يوم` };
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', p: 3, minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: '4px solid #f39c12',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
              color: 'white',
              mr: 2
            }}
          >
            <NotificationsIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.5 }}>
              تنبيهات المستندات
            </Typography>
            <Typography variant="body2" sx={{ color: '#95a5a6' }}>
              <Chip 
                label={`${data.length} مستند`} 
                size="small" 
                sx={{ 
                  bgcolor: '#fff3cd', 
                  color: '#f39c12',
                  fontWeight: 'bold'
                }} 
              /> قريب من الانتهاء
            </Typography>
          </Box>
        </Box>

        {/* Alerts List */}
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ opacity: 0.3, mb: 2 }}>✅</Typography>
            <Typography color="textSecondary">لا توجد مستندات قريبة من الانتهاء</Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              جميع المستندات صالحة لأكثر من 14 يوم
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {data.map((doc, index) => {
                const daysRemaining = getDaysRemaining(doc.expiryDate);
                const status = getStatusColor(daysRemaining);
                
                return (
                  <React.Fragment key={doc._id}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: status.bg,
                          color: status.color,
                          mr: 2,
                          flexShrink: 0
                        }}
                      >
                        {daysRemaining < 0 ? <WarningIcon /> : <CalendarIcon />}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>
                            {doc.documentName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ color: '#7f8c8d', display: 'block' }}>
                              رقم: {doc.documentNumber}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#95a5a6', display: 'block' }}>
                              {format(new Date(doc.expiryDate), 'dd/MM/yyyy', { locale: ar })}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{
                          bgcolor: status.bg,
                          color: status.color,
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>

            {/* View All Button */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/settings/document-alerts')}
                sx={{
                  borderColor: '#f39c12',
                  color: '#f39c12',
                  '&:hover': {
                    borderColor: '#e67e22',
                    bgcolor: 'rgba(243, 156, 18, 0.05)'
                  }
                }}
              >
                عرض جميع المستندات
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentAlertsWidget;